import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight, ChevronLeft, ChevronRight, CircleAlert, ExternalLink, ImageOff, Images, LayoutGrid, Link2,
  List, Loader2, Play, Search, X,
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { apiError, getScrape, getTaxonomy, listCatalogueBrands, listScrapes, setScrapeLink } from '../../api/catalogue';
import ClassificationView, { CategoryLine } from './ClassificationView';
import ProductFilters, { EMPTY_PRODUCT_FILTERS, productFilterParams } from './ProductFilters';
import {
  CATALOGUE_CSS, FILTERS, MAX_TRIES, Notice, PageHeader, PrimaryButton, STATUS, SecondaryButton,
  StatusPill, Thumb, ago, focusRing, host, isCopyJob, looksLikeImage, num, rupees, slotLabel, thumbOf, useScrapeRun,
} from './shared';

// Catalogue -> Full catalogue: every design in the catalogue, what its page
// gave, and a panel per design to check it, fix its link and scrape it again.
// Grid shows it the way a shopper would see it; List is the working view.
// Prices are ZOCS's own (from inbounding), never the scraped page's.

const PAGE = 50;
const VIEW_KEY = 'zocs_catalogue_view_mode';
const COLS = 'md:grid-cols-[minmax(0,2.3fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,2fr)_76px]';

function ResultRow({ r, onOpen }) {
  return (
    <button onClick={() => onOpen(r.design_id)}
      className={`w-full text-left grid grid-cols-1 ${COLS} gap-x-4 gap-y-2 items-center px-4 py-3 border-t border-gray-100 hover:bg-gray-50/80 transition-colors ${focusRing} focus-visible:ring-inset`}>
      <div className="flex items-center gap-3 min-w-0">
        <Thumb url={r.image_url_1} />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-gray-900 truncate">
            <span className="font-mono text-gray-400 mr-1.5">{r.design_code}</span>{r.design_name}
          </p>
          <p className="text-[12px] text-gray-500 truncate">{r.brand}{r.color ? ` · ${r.color}` : ''}</p>
          <CategoryLine catalog={r.catalog} className="mt-0.5" />
        </div>
      </div>
      <div className="min-w-0 text-[12.5px]">
        {r.product_link ? (
          <>
            <p className="truncate text-gray-700">{host(r.product_link)}</p>
            <p className="text-[11.5px] text-gray-400">{slotLabel(r.link_source)}</p>
          </>
        ) : <p className="text-gray-400">No product page found</p>}
      </div>
      <div>
        <StatusPill status={r.status} />
        {r.status === 'failed' && <p className="mt-1 text-[11px] text-gray-400">{r.attempts} of {MAX_TRIES} tries</p>}
      </div>
      <div className="min-w-0 text-[12.5px]">
        {r.status === 'done' ? (
          <>
            <p className="truncate text-gray-800">{r.title || 'No title on the page'}</p>
            <p className="mt-0.5 flex items-center gap-2 text-[11.5px] text-gray-500">
              <span className="inline-flex items-center gap-1"><Images size={12} aria-hidden />{r.image_count} photo{r.image_count === 1 ? '' : 's'}</span>
              <span className={r.has_description ? '' : 'text-gray-300'}>{r.has_description ? '· description' : '· no description'}</span>
            </p>
          </>
        ) : r.last_error ? (
          <p className="text-gray-500 line-clamp-2">{r.last_error}</p>
        ) : <p className="text-gray-300">—</p>}
      </div>
      <div className="text-[12px] text-gray-400 tabular-nums md:text-right">{ago(r.last_attempt_at)}</div>
    </button>
  );
}

// The design's ZOCS price: the lowest a shopper pays today across the units
// we hold, "from" when they differ, MRP struck through when it's notably higher.
function Price({ p, className = '' }) {
  if (!p || p.price == null) return <p className={`text-[12.5px] text-gray-400 ${className}`}>No price in ZOCS</p>;
  return (
    <p className={`flex flex-wrap items-baseline gap-x-1.5 ${className}`}>
      {p.price_max > p.price && <span className="text-[11.5px] text-gray-500">from</span>}
      <span className="text-[14px] font-semibold text-gray-900 tabular-nums">{rupees(p.price)}</span>
      {p.mrp > p.price * 1.1 && <s className="text-[12px] text-gray-400 tabular-nums">{rupees(p.mrp)}</s>}
    </p>
  );
}

function ProductCard({ r, onOpen }) {
  const image = r.image || (looksLikeImage(r.image_url_1) ? r.image_url_1 : null);
  const outOfStock = r.zocs?.price != null && r.zocs.in_stock === 0;
  const sizes = (r.zocs?.sizes || []).filter((s) => s.on_hand > 0).map((s) => s.size);
  return (
    <button onClick={() => onOpen(r.design_id)}
      className={`group text-left rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-gray-300 hover:shadow-sm transition ${focusRing}`}>
      <div className="relative aspect-[4/5] bg-gray-50">
        {image
          ? <img src={thumbOf(image, 480)} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
          : <div className="h-full w-full flex items-center justify-center text-gray-300"><ImageOff size={22} aria-hidden /></div>}
        {r.status !== 'done' && <span className="absolute left-2 top-2"><StatusPill status={r.status} /></span>}
        {outOfStock && (
          <span className="absolute right-2 top-2 inline-flex items-center h-6 px-2 rounded-md bg-gray-900/80 text-[11.5px] font-medium text-white">
            Out of stock
          </span>
        )}
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 truncate">{r.brand}</p>
        <p className={`mt-0.5 min-h-[2.6em] text-[13px] leading-snug line-clamp-2 ${r.title ? 'text-gray-900' : 'text-gray-500'}`}>
          {r.title || r.design_name}
        </p>
        <Price p={r.zocs} className="mt-1.5" />
        <CategoryLine catalog={r.catalog} className="mt-1" />
        {sizes.length > 0 && <p className="mt-1 text-[11.5px] text-gray-500 truncate" title={sizes.join(', ')}>{sizes.join(' · ')}</p>}
      </div>
    </button>
  );
}

// What ZOCS holds of the design, size by size (skus + inventory_items).
function ZocsSizes({ zocs, scraped }) {
  if (!zocs?.sizes?.length) return <p className="text-[12.5px] text-gray-400">No units of this design in ZOCS.</p>;
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400">
          <th className="font-semibold pb-1.5">Size</th>
          <th className="font-semibold pb-1.5 text-right">Price</th>
          <th className="font-semibold pb-1.5 text-right">On hand</th>
          {scraped && <th className="font-semibold pb-1.5 text-right">On the page</th>}
        </tr>
      </thead>
      <tbody>
        {zocs.sizes.map((s) => (
          <tr key={s.size} className={`border-t border-gray-100 ${s.on_hand ? 'text-gray-700' : 'text-gray-400'}`}>
            <td className="py-1.5">{s.size}</td>
            <td className="py-1.5 text-right tabular-nums">{rupees(s.price)}</td>
            <td className="py-1.5 text-right tabular-nums">{s.on_hand ? num(s.on_hand) : 'none'}</td>
            {scraped && (
              <td className="py-1.5 text-right" title={s.on_page && s.on_page !== s.size ? `The brand calls ${s.size} "${s.on_page}"` : undefined}>
                {s.on_page || <span className="text-gray-400">not on the page</span>}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ all, query, filtered, onPull }) {
  return (
    <div className="px-4 py-12 text-center">
      <p className="text-[13px] font-medium text-gray-700">{filtered ? 'No designs match these filters' : all === 0 ? 'Nothing in the catalogue yet' : 'No designs here'}</p>
      <p className="mt-1 text-[12.5px] text-gray-400">
        {filtered ? 'Change or clear a filter to see more products.' : all === 0
          ? <>Pull designs in from <button onClick={onPull} className="text-zoddle-green font-medium hover:underline">Pull & scrape</button>.</>
          : query ? 'Try another search.' : 'Pick another status.'}
      </p>
    </div>
  );
}

function Section({ title, children, aside }) {
  return (
    <section className="px-5 py-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2.5">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</h4>
        {aside}
      </div>
      {children}
    </section>
  );
}

function DesignDrawer({ designId, running, refreshKey, onClose, onChanged, onScrapeNow }) {
  const { navigate } = useNavigation();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const x = await getScrape(designId);
      setD(x); setLink(x.product_link || ''); setError('');
    } catch (e) {
      setError(apiError(e, 'Could not load this design'));
    }
  }, [designId]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function saveLink() {
    setSaving(true); setNote(''); setError('');
    try {
      await setScrapeLink(designId, link.trim() || null);
      await load();
      onChanged();
      setNote(link.trim() ? 'Link saved. The design is waiting to be scraped.' : 'Link removed.');
    } catch (e) {
      setError(apiError(e, 'Could not save the link'));
    } finally {
      setSaving(false);
    }
  }

  const src = d?.data?.sources?.[d?.data?.primary];
  const linkChanged = d && (link.trim() || null) !== (d.product_link || null);
  const zocsImages = (d?.images_now || []).filter(looksLikeImage);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label="Design details">
      <div className="absolute inset-0 bg-gray-900/20 catalogue-fade" onClick={onClose} />
      <aside className="relative h-full w-full sm:w-[540px] bg-white shadow-2xl border-l border-gray-200 flex flex-col catalogue-drawer">
        <header className="px-5 py-4 flex items-start gap-3 border-b border-gray-100">
          <Thumb url={zocsImages[0]} size="h-12 w-12" />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-gray-900 leading-snug">
              {d ? <><span className="font-mono text-gray-400 mr-1.5">{d.design_code}</span>{d.design_name}</> : 'Loading…'}
            </p>
            <div className="mt-1 flex items-center gap-2 text-[12.5px] text-gray-500">
              {d && <StatusPill status={d.status} />}
              {d?.brand}
            </div>
            {d && (
              <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-[12px] text-gray-400">
                <Price p={d.zocs} />
                {d.zocs?.price != null && (
                  <span>{d.zocs.in_stock ? `${num(d.zocs.in_stock)} in stock` : 'out of stock'} · price from ZOCS</span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" className={`h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 ${focusRing}`}>
            <X size={17} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {error && <Notice icon={CircleAlert} className="mx-5 mt-4">{error}</Notice>}

          {d && (
            <>
              <Section title="Product link"
                aside={d.product_link && (
                  <a href={d.product_link} target="_blank" rel="noreferrer"
                    className={`inline-flex items-center gap-1 text-[12px] font-medium text-zoddle-blue hover:underline rounded ${focusRing}`}>
                    Open page <ExternalLink size={12} />
                  </a>
                )}>
                <div className="flex gap-2">
                  <label className="flex-1 min-w-0 flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 focus-within:ring-2 focus-within:ring-zoddle-green/30">
                    <Link2 size={14} className="text-gray-400 shrink-0" aria-hidden />
                    <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://brand.com/products/…"
                      aria-label="Product link" className="flex-1 min-w-0 text-[13px] outline-none bg-transparent" />
                  </label>
                  <PrimaryButton onClick={saveLink} disabled={!linkChanged || saving || running}>
                    {saving && <Loader2 size={14} className="motion-safe:animate-spin" />}Save
                  </PrimaryButton>
                </div>
                <p className="mt-1.5 text-[12px] text-gray-400">
                  {running ? 'Links can be changed once the scrape run finishes.' : slotLabel(d.link_source) ? `Found ${slotLabel(d.link_source)}.` : ' '}
                </p>
                {note && <p className="mt-1 text-[12px] text-green-700">{note}</p>}
                {d.last_error && (
                  <Notice tone="gray" icon={CircleAlert} className="mt-3">
                    {d.last_error}{d.status === 'failed' && <span className="text-gray-400"> · {d.attempts} of {MAX_TRIES} tries</span>}
                  </Notice>
                )}
                {d.product_link && (
                  <SecondaryButton className="mt-3" onClick={() => onScrapeNow(designId)} disabled={running || linkChanged}>
                    <Play size={13} className="fill-current" /> {d.status === 'done' ? 'Scrape again' : 'Scrape this design now'}
                  </SecondaryButton>
                )}
              </Section>

              <Section title="Website classification"
                aside={(
                  <button onClick={() => navigate('catalogueattributes', { designId })}
                    className={`inline-flex items-center gap-1 text-[12px] font-medium text-zoddle-blue hover:underline rounded ${focusRing}`}>
                    Review &amp; edit <ArrowRight size={12} />
                  </button>
                )}>
                <ClassificationView catalog={d.catalog} />
              </Section>

              <Section title="Sizes in ZOCS">
                <ZocsSizes zocs={d.zocs} scraped={!!src} />
              </Section>

              {src ? (
                <>
                  <Section title={`What ${host(src.url)} says`} aside={<span className="text-[11.5px] text-gray-400">{ago(src.fetched_at)}</span>}>
                    <p className="text-[14px] font-medium text-gray-900 leading-snug">{src.title || 'No title on the page'}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-gray-500">
                      {src.colour && <span>Colour <b className="text-gray-800 font-medium">{src.colour}</b></span>}
                      {src.article_type && <span>Type <b className="text-gray-800 font-medium">{src.article_type}</b></span>}
                    </div>
                    {src.description
                      ? <p className="mt-3 text-[13px] leading-relaxed text-gray-700 whitespace-pre-line">{src.description}</p>
                      : <p className="mt-3 text-[12.5px] text-gray-400">The page has no description.</p>}
                  </Section>

                  <Section title={`Photos on the page · ${src.images.length}`}>
                    {src.images.length ? (
                      <div className="grid grid-cols-3 gap-2">
                        {src.images.slice(0, 24).map((u) => (
                          <a key={u} href={u} target="_blank" rel="noreferrer" className={`block rounded-lg overflow-hidden ring-1 ring-gray-200 bg-gray-50 ${focusRing}`}>
                            <img src={thumbOf(u, 300)} alt="" loading="lazy" referrerPolicy="no-referrer" className="aspect-square w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    ) : <p className="text-[12.5px] text-gray-400">The page gave no photos.</p>}
                  </Section>

                  {src.variants?.length > 0 && (
                    <Section title={`Sizes on the page · ${src.variants.length}`}>
                      <ul className="flex flex-wrap gap-1.5">
                        {src.variants.slice(0, 40).map((v, i) => (
                          <li key={i} className="inline-flex items-center h-7 px-2.5 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 text-[12.5px] text-gray-700">
                            {v.labels?.length ? v.labels.join(' · ') : '—'}
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}
                </>
              ) : (
                <Section title="What the page says">
                  <p className="text-[12.5px] text-gray-400">
                    {d.status === 'no_link' ? 'Add a product link above to scrape this design.'
                      : d.status === 'pending' ? 'Not scraped yet — it is waiting for the next run.'
                        : 'Nothing was read from this page.'}
                  </p>
                </Section>
              )}

              {zocsImages.length > 0 && (
                <Section title="Photos in ZOCS now">
                  <div className="flex gap-2">
                    {zocsImages.map((u) => <Thumb key={u} url={u} size="h-16 w-16" />)}
                  </div>
                </Section>
              )}

              {d.data && (
                <Section title="Raw page data">
                  <details>
                    <summary className={`cursor-pointer text-[12.5px] text-gray-500 hover:text-gray-700 rounded ${focusRing}`}>
                      Show everything the scraper stored
                    </summary>
                    <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-gray-50 border border-gray-100 p-3 text-[11px] leading-relaxed text-gray-600">
                      {JSON.stringify(d.data, null, 2)}
                    </pre>
                  </details>
                </Section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function FullCatalogue() {
  const { navData, navigate } = useNavigation();
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState(() => (FILTERS.includes(navData?.status) ? navData.status : 'done'));
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tax, setTax] = useState(null);
  const [taxError, setTaxError] = useState('');
  const [productFilters, setProductFilters] = useState(EMPTY_PRODUCT_FILTERS);
  const [brands, setBrands] = useState(null);
  const [attribute, setAttribute] = useState('');
  const [loading, setLoading] = useState(false);
  const listRequest = useRef(0);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
  });
  const switchView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window: the choice just isn't kept */ }
  };

  const loadList = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    try {
      const params = { offset: page * PAGE, limit: PAGE, ...productFilterParams(productFilters) };
      if (filter !== 'all') params.status = filter;
      if (q) params.q = q;
      const result = await listScrapes(params);
      if (request !== listRequest.current) return;
      setList(result);
      setLoadError('');
    } catch (e) {
      if (request !== listRequest.current) return;
      setLoadError(apiError(e, 'Could not load the catalogue'));
      setList(null);
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  }, [filter, q, page, productFilters]);

  const scrape = useScrapeRun((what) => {
    loadList();
    if (what === 'finished') setRefreshKey((k) => k + 1);
  });

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    let active = true;
    getTaxonomy().then((value) => { if (active) setTax(value); })
      .catch((e) => { if (active) setTaxError(apiError(e, 'Could not load product filters')); });
    // Brand filter options; without them the filter row just has no Brand box.
    listCatalogueBrands().then((list) => { if (active) setBrands((list || []).map((b) => b.name).filter(Boolean)); })
      .catch(() => {});
    return () => { active = false; listRequest.current += 1; };
  }, []);
  // Opened from Pull & scrape on one status: navigate('cataloguedata', { status }).
  useEffect(() => {
    if (navData?.status && FILTERS.includes(navData.status)) { setFilter(navData.status); setPage(0); }
  }, [navData]);
  useEffect(() => {
    const t = setTimeout(() => { setQ(query.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const closeDrawer = useCallback(() => setOpenId(null), []);
  const counts = list?.counts || {};
  const all = Object.values(counts).reduce((s, v) => s + v, 0);
  const total = list?.total ?? 0;
  const filtered = Boolean(q || productFilters.category || productFilters.occasion || Object.keys(productFilters.attributes).length);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px]">
      <style>{CATALOGUE_CSS}</style>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader title="Full catalogue">
          Every design in the catalogue and what its product page gave. Open one to check it, fix its link or scrape it again.
        </PageHeader>
        {scrape.running && (
          <button onClick={() => navigate('cataloguepull')}
            className={`inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-sky-50 text-[12.5px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200 hover:bg-sky-100 ${focusRing}`}>
            <Loader2 size={14} className="motion-safe:animate-spin" />
            {isCopyJob(scrape.run) ? `Copying photos for ${num(scrape.run.total)} designs` : `Scraping ${num(scrape.run.total)} links`} <ArrowRight size={13} />
          </button>
        )}
      </div>

      {loadError && <Notice icon={CircleAlert} className="mt-4">{loadError}</Notice>}
      {taxError && <Notice icon={CircleAlert} className="mt-4">{taxError}</Notice>}
      {scrape.error && <Notice icon={CircleAlert} className="mt-4">{scrape.error}</Notice>}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter by status">
          {FILTERS.map((f) => {
            const n = f === 'all' ? all : counts[f] || 0;
            const on = filter === f;
            return (
              <button key={f} role="tab" aria-selected={on} onClick={() => { setFilter(f); setPage(0); }}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-medium border transition ${on ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'} ${focusRing}`}>
                {f !== 'all' && <span className={`h-1.5 w-1.5 rounded-full ${STATUS[f].bar}`} aria-hidden />}
                {f === 'all' ? 'All' : STATUS[f].label}
                <span className={`tabular-nums ${on ? 'text-gray-300' : 'text-gray-400'}`}>{num(n)}</span>
              </button>
            );
          })}
        </div>
        <div className="flex w-full sm:w-auto items-center gap-2">
          <label className="flex flex-1 items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white sm:w-80 focus-within:ring-2 focus-within:ring-zoddle-green/30">
            <Search size={14} className="text-gray-400 shrink-0" aria-hidden />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search design code, name or link"
              className="flex-1 min-w-0 text-[13px] outline-none bg-transparent placeholder:text-gray-400" />
            {query && <button onClick={() => setQuery('')} aria-label="Clear search" className="text-gray-400 hover:text-gray-600"><X size={14} /></button>}
          </label>
          <div className="flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="View">
            {[['grid', LayoutGrid, 'Grid'], ['list', List, 'List']].map(([v, Icon, label]) => (
              <button key={v} onClick={() => switchView(v)} aria-pressed={view === v} title={label}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12.5px] font-medium transition ${view === v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'} ${focusRing}`}>
                <Icon size={14} aria-hidden />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {tax && <ProductFilters tax={tax} filters={productFilters} attribute={attribute} onAttributeChange={setAttribute} brands={brands}
        onChange={(value) => { setProductFilters(value); setPage(0); }} />}
      <p className="mt-3 text-[12.5px] text-gray-500" role="status" aria-live="polite">
        {loading ? 'Updating products…' : loadError ? 'Product count unavailable' : `${num(total)} matching design${total === 1 ? '' : 's'}`}
      </p>

      <div aria-busy={loading} className={loading && list ? 'opacity-50 pointer-events-none' : ''}>
      {view === 'grid' ? (
        <div className="mt-3">
          {!list && !loadError && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <div className="aspect-[4/5] bg-gray-100 motion-safe:animate-pulse" />
                  <div className="p-3 space-y-1.5">
                    <div className="h-2.5 w-1/3 rounded bg-gray-100 motion-safe:animate-pulse" />
                    <div className="h-3 w-4/5 rounded bg-gray-100 motion-safe:animate-pulse" />
                    <div className="h-3 w-1/4 rounded bg-gray-100 motion-safe:animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {list && list.rows.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white">
              <Empty all={all} query={query} filtered={filtered} onPull={() => navigate('cataloguepull')} />
            </div>
          )}
          {list && list.rows.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {list.rows.map((r) => <ProductCard key={r.design_id} r={r} onOpen={setOpenId} />)}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className={`hidden md:grid ${COLS} gap-x-4 px-4 h-9 items-center text-[11px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50/70`}>
            <span>Design</span><span>Product link</span><span>Status</span><span>What the page gave</span><span className="text-right">Updated</span>
          </div>
          {!list && !loadError && Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="border-t border-gray-100 px-4 py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-gray-100 motion-safe:animate-pulse" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-1/3 rounded bg-gray-100 motion-safe:animate-pulse" />
                <div className="h-3 w-1/5 rounded bg-gray-100 motion-safe:animate-pulse" />
              </div>
            </div>
          ))}
          {list && list.rows.length === 0 && (
            <div className="border-t border-gray-100">
              <Empty all={all} query={query} filtered={filtered} onPull={() => navigate('cataloguepull')} />
            </div>
          )}
          {list && list.rows.map((r) => <ResultRow key={r.design_id} r={r} onOpen={setOpenId} />)}
        </div>
      )}
      </div>

      {total > PAGE && (
        <div className="mt-3 flex items-center justify-end gap-2 text-[12.5px] text-gray-500">
          <span className="tabular-nums">{num(page * PAGE + 1)}–{num(Math.min(total, (page + 1) * PAGE))} of {num(total)}</span>
          <SecondaryButton className="h-8 px-2.5" onClick={() => setPage(page - 1)} disabled={loading || page === 0} aria-label="Previous page"><ChevronLeft size={15} /></SecondaryButton>
          <SecondaryButton className="h-8 px-2.5" onClick={() => setPage(page + 1)} disabled={loading || (page + 1) * PAGE >= total} aria-label="Next page"><ChevronRight size={15} /></SecondaryButton>
        </div>
      )}

      {openId && (
        <DesignDrawer designId={openId} running={scrape.running} refreshKey={refreshKey}
          onClose={closeDrawer} onChanged={loadList} onScrapeNow={(id) => scrape.start('selected', [id])} />
      )}
    </div>
  );
}
